import React, { useState } from 'react';

import { useToast } from '@chakra-ui/react';

import {
  deleteCollectionPoints,
  fetchPaginatedCollectionPoints,
} from 'api/location';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';

import { CollectionPointsProps } from './CollectionPoints.props';

import CollectionPointsView from './CollectionPoints.view';

const CollectionPointsContainer = (_: CollectionPointsProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(1);

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const queryKey = [`fetch-collection-points-${washHubId}`, page];

  const queryFn = () =>
    fetchPaginatedCollectionPoints({
      page,
      limit: 10,
      washHub: washHubId,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  const { mutate, isLoading: isDeleting } = useMutation(deleteCollectionPoints);

  useWashHubListener(() => {
    setPage(1);
  });

  const points = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const onDelete = (ids: string[], _onSuccess: () => void) =>
    mutate(ids, {
      onSuccess: () => {
        toast({
          duration: 3000,
          isClosable: true,
          status: 'success',
          title: 'Success',
          description: 'Collection Points Deleted',
        });

        queryClient.invalidateQueries(`fetch-collection-points-${washHubId}`);
        queryClient.invalidateQueries(
          `fetch-collection-points-${washHubId}-select`,
          {
            exact: true,
          },
        );

        _onSuccess();
      },
    });

  const _props = {
    points,
    isLoading: isLoading || isFetching,
    isDeleting,
    pagination,
    onDelete,
  };

  return <CollectionPointsView {..._props} />;
};

export default CollectionPointsContainer;
